import React from 'react';
import { useNavigate } from 'react-router-dom';
import TemplateList from '../components/TemplateList';
import { Btn, Card, Note, PageBand, SectionHead } from '../components/ui';
import { useEmailManager } from '../index';

/**
 * `/admin/email/templates` — the nine templates the platform can send.
 *
 * A catalog, and only a catalog. It used to carry the editor behind it as a detail view, which put
 * the one screen where copy is written on a different route from the campaign that sends it — so
 * the template an operator had just edited had to be re-chosen in the builder to be sure it was the
 * one going out. The editor is the first card of the setup page now, and opening a template here is
 * a link to it.
 *
 * The frame is reached by **View Templates** on the dashboard. It stays a page of its own because
 * browsing what exists is a different question from composing one send: all but the campaign are
 * fired by an account or billing event and can never be sent to an audience. The builder used to
 * list them as disabled options; it no longer shows them at all, which makes this page the one
 * place they can be found — and the reason it must keep listing every one of them.
 */
const Templates = () => {
  const navigate = useNavigate();
  const { templates, selectedKey, failed, loadOk, loadTemplates } = useEmailManager();

  /*
    Opening a template is a navigation, not a parameter change.

    It sets `?template=` on the setup page, which is what the editor and the builder both read — so
    one link chooses the template in both, and the link itself is shareable.
  */
  const open = (key, variant) =>
    navigate(
      `/admin/email/setup?template=${encodeURIComponent(key)}${
        variant ? `&variant=${encodeURIComponent(variant)}` : ''
      }`
    );

  return (
    <>
      <PageBand
        brand
        title="Email Templates"
        subtitle="The nine templates the platform sends, and the copy each one carries"
        actions={
          <>
            <Btn variant="outline" onClick={() => navigate('/admin/email')}>
              Back to dashboard
            </Btn>
            <Btn icon="Plus" onClick={() => navigate('/admin/email/setup')}>
              Create Campaign
            </Btn>
          </>
        }
      />

      <Card id="catalog">
        {/*
          No Upload Custom HTML control.

          It sat here disabled, with a tooltip explaining that uploaded HTML would bypass the
          shared components. That reasoning still holds — it is why the control was never built —
          but a permanently disabled button is not how to say it. It occupies the one prominent
          position in this header and invites a click from every operator who has not read the
          tooltip, to tell them about a feature that does not exist. The rule itself is recorded in
          the Settings note, where a decision belongs.
        */}
        <SectionHead icon="Mail" title="Email Templates (Editable)" />
        <TemplateList
          templates={templates}
          selected={selectedKey}
          onSelect={(key) => open(key)}
          onPreview={(key, variant) => open(key, variant)}
          loading={loadOk.templates === undefined}
          failed={failed('templates')}
          onRetry={loadTemplates}
        />
        <Note>
          Editing a template opens it on the setup page, with its own preview beside the fields.
          Only the campaign template can be sent to an audience, and only it opens with the campaign
          builder and the scheduler; the other eight go out in response to an account or billing
          event, so there is nothing about them to aim or schedule.
        </Note>
      </Card>
    </>
  );
};

export default Templates;
